import { useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';

import { SHOULD_REFRESH } from '@providers/LineItemsProvider/constants';
import { LineItemsContext } from '@providers/LineItemsProvider/LineItemsContext';
import { updateLineItemColumnLabels } from '@features/event-level-column-rename/api/event-level-column-rename-client';
import { buildLabelPayload } from '@features/event-level-column-rename/utils/build-label-payload';
import { COLUMN_RENAME_TELEMETRY_EVENT, trackColumnRename } from '@features/event-level-column-rename/utils/column-rename-telemetry';
import { alertMessage } from '@utils/common-new/alertMessage';
import type { AxiosError } from 'axios';

const SAVED_ALERT_SECONDS = 5;

/**
 * ACTION (Building Code "Golden Path"): the complete response to "save this event's column
 * labels" — the write, plus every consequence: refresh the event so all grids repaint, and the
 * success toast. Components fire this action; they do not orchestrate consequences. The one
 * consequence that stays component-side is 422 surfacing: rejection messages render at the form
 * fields (preserving what the buyer typed), which a toast cannot do.
 *
 * The buyer event payload is not held in the TanStack cache — it is loaded by
 * LineItemsProvider and stored on RequestContext — so there is no query key to invalidate.
 * `SHOULD_REFRESH` is the reload lever those providers actually listen to, and dispatching it
 * only after the PATCH resolves matters: the reload re-reads the event, so firing it earlier
 * races the write and reads back the pre-save labels.
 */
export const useUpdateLineItemColumnLabels = () => {
  const { dispatch, request } = useContext(LineItemsContext);
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: updateLineItemColumnLabels,
    onError: error => {
      trackColumnRename(COLUMN_RENAME_TELEMETRY_EVENT.saveFailed, request, {
        httpStatus: (error as AxiosError | undefined)?.response?.status ?? null,
      });
    },
    onSuccess: (_, variables) => {
      dispatch({ type: SHOULD_REFRESH });
      alertMessage(t('messages.saved'), 'success', SAVED_ALERT_SECONDS);
      trackColumnRename(COLUMN_RENAME_TELEMETRY_EVENT.saved, request, {
        values: buildLabelPayload(variables.values).line_item_column_labels,
      });
    },
  });
};
